import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import DateRangeFilterItem from '../../../delivery/components/DateRangeFilterItem';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { GET_ALL_CHANNEL_LITE, allChannelLiteResponse } from '../../../../graphql/queries/getChannelAll';
import { GET_HUB_LIST, GetHubListResponse } from '../../../wms/bags/graphql/queries/getHubList';
import {
    GET_CLIENT_CARTON_ARRIVE_PORT_CODES,
    GetClientCartonArrivePortCodesResponse,
} from '../graphql/queries/getClientCartons';

export interface ClientCartonFilterOptions {
    bigBagNo: string;
    masterWaybillNo: string;
    channelId: string | 'all';
    arrivePortCode: string | 'all';
    scanWarehouseId: string | 'all';
    createdAtRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
    scanTimeRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
}

export const DEFAULT_FILTER_OPTIONS: ClientCartonFilterOptions = {
    bigBagNo: '',
    masterWaybillNo: '',
    channelId: 'all',
    arrivePortCode: 'all',
    scanWarehouseId: 'all',
    createdAtRange: [null, null],
    scanTimeRange: [dayjs().subtract(1, 'week'), dayjs()],
};

interface FilterAreaProps {
    filters: ClientCartonFilterOptions;
    setFilters: (filters: ClientCartonFilterOptions) => void;
}

const FilterArea: React.FC<FilterAreaProps> = ({ filters, setFilters }) => {
    const { t } = useTranslation();

    const { data: channelData, loading: channelLoading } = useQuery<allChannelLiteResponse>(GET_ALL_CHANNEL_LITE);
    const { data: portCodeData, loading: portCodeLoading } = useQuery<GetClientCartonArrivePortCodesResponse>(GET_CLIENT_CARTON_ARRIVE_PORT_CODES);
    const { data: hubData, loading: hubLoading } = useQuery<GetHubListResponse>(GET_HUB_LIST);

    const channelOptions = React.useMemo(() => {
        const all = { value: 'all', label: t('wms.clientCartons.options.all') };
        if (!channelData?.getAllChannelsLite) return [all];
        return [all, ...channelData.getAllChannelsLite.map(ch => ({ value: ch.id, label: ch.channelName }))];
    }, [channelData, t]);

    const arrivePortCodeOptions = React.useMemo(() => {
        const all = { value: 'all', label: t('wms.clientCartons.options.all') };
        if (!portCodeData?.getClientCartonArrivePortCodes) return [all];
        return [all, ...portCodeData.getClientCartonArrivePortCodes.map(code => ({ value: code, label: code }))];
    }, [portCodeData, t]);

    const hubOptions = React.useMemo(() => {
        const all = { value: 'all', label: t('wms.clientCartons.options.all') };
        if (!hubData?.hubList) return [all];
        return [all, ...hubData.hubList
            .filter(hub => hub.sortingCode)
            .map(hub => {
                const firstSegment = hub.sortingCode?.split('-')[0] || '';
                return { value: hub.id, label: `${hub.name} (${firstSegment})` };
            })
            .filter(opt => opt.value)];
    }, [hubData, t]);

    const getDateRangeString = (
        range: [dayjs.Dayjs | null, dayjs.Dayjs | null]
    ): [string | null, string | null] | null => {
        if (!range) return null;
        return [
            range[0] ? range[0].format('YYYY/MM/DD') : null,
            range[1] ? range[1].format('YYYY/MM/DD') : null,
        ];
    };

    const createdAtRange = getDateRangeString(filters.createdAtRange);
    const scanTimeRange = getDateRangeString(filters.scanTimeRange);

    const labelCartonNo = String(t('wms.clientCartons.filters.cartonNo'));
    const labelMasterWaybillNo = String(t('wms.clientCartons.filters.masterWaybillNo'));
    const labelCustomerName = String(t('wms.clientCartons.filters.customerName'));
    const labelCreatedTime = String(t('wms.clientCartons.filters.createdTime'));
    const labelScanTime = String(t('wms.clientCartons.filters.scanTime'));
    const labelDestinationPort = String(t('wms.clientCartons.filters.destinationPort'));
    const labelScanPort = String(t('wms.clientCartons.filters.scanPort'));

    const col1Labels = [labelCartonNo, labelDestinationPort];
    const col2Labels = [labelMasterWaybillNo, labelScanPort];
    const col3Labels = [labelCustomerName, labelScanTime];
    const col4Labels = [labelCreatedTime];
    return (
        <div className="mb-2 pl-0 py-2">
            <div className="grid grid-cols-4 gap-x-10 gap-y-4 items-start">
                {/* Row 1 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelCartonNo}
                        value={filters.bigBagNo}
                        onChange={(e) => setFilters({ ...filters, bigBagNo: e.target.value })}
                        placeholder=""
                        spanClassName={getSmartLabelClassName(col1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelMasterWaybillNo}
                        value={filters.masterWaybillNo}
                        onChange={(e) => setFilters({ ...filters, masterWaybillNo: e.target.value })}
                        placeholder=""
                        spanClassName={getSmartLabelClassName(col2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelCustomerName}
                        options={channelOptions}
                        value={filters.channelId}
                        onChange={(value: string) => setFilters({ ...filters, channelId: value || 'all' })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col3Labels)}
                        loading={channelLoading}
                    />
                </div>
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={labelCreatedTime}
                        value={createdAtRange}
                        onChange={(dates) => {
                            const dayjsRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] = dates
                                ? [
                                    dates[0] ? dayjs(dates[0], 'YYYY/MM/DD') : null,
                                    dates[1] ? dayjs(dates[1], 'YYYY/MM/DD') : null,
                                ]
                                : [null, null];
                            setFilters({ ...filters, createdAtRange: dayjsRange });
                        }}
                        spanClassName={getSmartLabelClassName(col4Labels)}
                    />
                </div>

                {/* Row 2 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelDestinationPort}
                        options={arrivePortCodeOptions}
                        value={filters.arrivePortCode}
                        onChange={(value: string) => setFilters({ ...filters, arrivePortCode: value || 'all' })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col1Labels)}
                        loading={portCodeLoading}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelScanPort}
                        options={hubOptions}
                        value={filters.scanWarehouseId}
                        onChange={(value: string) => setFilters({ ...filters, scanWarehouseId: value || 'all' })}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col2Labels)}
                        loading={hubLoading}
                    />
                </div>
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={labelScanTime}
                        value={scanTimeRange}
                        onChange={(dates) => {
                            const dayjsRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] = dates
                                ? [
                                    dates[0] ? dayjs(dates[0], 'YYYY/MM/DD') : null,
                                    dates[1] ? dayjs(dates[1], 'YYYY/MM/DD') : null,
                                ]
                                : [null, null];
                            setFilters({ ...filters, scanTimeRange: dayjsRange });
                        }}
                        spanClassName={getSmartLabelClassName(col3Labels)}
                    />
                </div>
            </div>
        </div>
    );
};

export default FilterArea;
